import {
  Box,
  Button,
  Divider,
  Group,
  Paper,
  SegmentedControl,
  Space,
  Tabs,
  Text,
} from "@mantine/core";
import { IconDownload } from "@tabler/icons-react";
import { useState } from "react";
import DailyCutoffsTable from "./DailyCutoffsTable";
import useMeterCutoffs from "../../hooks/useMeterCutoffs";
import { DailyCutoffDataType, MonthlyCutoffDataType, MeterReturnType } from "../../types";
import MonthlyCutoffsTable from "./MonthlyCutoffsTable";
import { formatDate } from "../../utils/date";
import { fetchDailyCutoffsAsync, fetchMonthlyCutoffsAsync } from "../../actions/meters.actions";
import generateDailyCutoffsPdf from "./hooks/generateDailyCutoffsPdf";
import generateMonthlyCutoffsPdf from "./hooks/generateMonthlyCutoffsPdf";
import generateDailyCutoffsCsv from "./hooks/generateDailyCutoffsCsv";
import generateMonthlyCutoffsCsv from "./hooks/generateMonthlyCutoffsCsv";
import DownloadFormatModal from "../../components/modals/DownloadFormatModal";
import { logReportDownloadAsync } from "../../actions/sharedUrl.actions";

type MeterCutOffsViewProps = {
  meter: MeterReturnType;
};

export default function MeterCutOffsView({ meter }: MeterCutOffsViewProps) {
  const [showLiters, setShowLiters] = useState(false);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);

  const handleDownloadReport = async (format: "pdf" | "csv") => {
    const FETCH_ALL_LIMIT = 9999;
    if (tabValue === "daily") {
      const response = await fetchDailyCutoffsAsync(meter.meterSerialNo, 1, FETCH_ALL_LIMIT + 1);
      const res = (response as any)?.data;
      const rawData: any[] = res?.data || [];
      const displayData = rawData.slice(0, FETCH_ALL_LIMIT);

      const results: DailyCutoffDataType[] = displayData.map((item: any, i: number) => ({
        id: item?.id,
        deviceDateTime: item?.deviceTime,
        totalVolume: item?.totalVolume,
        previousVolume: rawData[i + 1]?.totalVolume ?? null,
        temperature: item?.temperature,
        errorCode: Number(item?.errorCode) || 0,
        isAlarmTelegram: item?.isAlarmTelegram || false,
        alarmSummary: item?.alarmSummary || null,
      }));

      if (format === "pdf") {
        generateDailyCutoffsPdf({ meterSerialNo: meter.meterSerialNo, results, showLiters });
      } else {
        generateDailyCutoffsCsv({ meterSerialNo: meter.meterSerialNo, results, showLiters });
      }
      logReportDownloadAsync({ meterSerialNo: meter.meterSerialNo, reportType: "daily", format });
    } else {
      const response = await fetchMonthlyCutoffsAsync(meter.meterSerialNo, 1, FETCH_ALL_LIMIT + 1);
      const res = (response as any)?.data;
      const rawData: any[] = res?.data || [];
      const displayData = rawData.slice(0, FETCH_ALL_LIMIT);

      const results: MonthlyCutoffDataType[] = displayData.map((item: any, i: number) => ({
        id: item?.id,
        month: item?.month,
        cutoffDateTime: item?.["CutOff Date-Time"],
        consumption: item?.["MonthlyConsumption"],
        reading: item?.["Consumtion @ CutOff"],
        previousReading: rawData[i + 1]?.["Consumtion @ CutOff"] ?? null,
      }));

      if (format === "pdf") {
        generateMonthlyCutoffsPdf({ meterSerialNo: meter.meterSerialNo, results, showLiters });
      } else {
        generateMonthlyCutoffsCsv({ meterSerialNo: meter.meterSerialNo, results, showLiters });
      }
      logReportDownloadAsync({ meterSerialNo: meter.meterSerialNo, reportType: "monthly", format });
    }
  };

  const {
    data,
    tabValue,
    setTabValue,
    handleDailyCutoffsPageChange,
    handleDailyCutoffsPageLimitSelect,
    handleMonthlyCutoffsPageChange,
    handleMonthlyCutoffsPageLimitSelect,
    goBack,
    onDailyCutoffRowClick,
    dailyCutoffRecordId
  } = useMeterCutoffs({ meter });

  return (
    <Box>
      <Text size="lg" fw={600}>
        M/S {meter?.meterSerialNo}
      </Text>
      <Text>
        Last reading:{" "}
        {meter?.Aggregate_smartMeterData?.[0]?.deviceTime
          ? formatDate(meter?.Aggregate_smartMeterData?.[0]?.deviceTime)
          : "--"}
      </Text>
      <Space h={"sm"} />
      <Tabs
        styles={() => ({
          tab: {
            color: "purple",
            fontWeight: 500,
            padding: "10px",
            borderBottomWidth: "4px",
          },
          list: {
            border: "none !important",
          },
        })}
        color="#FF4A84"
        value={tabValue}
        onChange={setTabValue}
      >
        <Group justify="space-between">
          <Paper
            radius={8}
            w={"fit-content"}
            px="xs"
            pt={2}
            pb={5}
            withBorder
            style={{
              borderColor: "purple",
            }}
          >
            <Tabs.List>
              <Tabs.Tab value="daily">Daily</Tabs.Tab>
              <Tabs.Tab value="monthly">Monthly</Tabs.Tab>
            </Tabs.List>
          </Paper>

          <Group gap="md">
            <SegmentedControl
              size="xs"
              value={showLiters ? "liters" : "units"}
              onChange={(val) => setShowLiters(val === "liters")}
              data={[
                { label: "Units", value: "units" },
                { label: "Liters", value: "liters" },
              ]}
            />
            <Button
              radius={8}
              size="sm"
              variant="outline"
              bg={"white"}
              fw={500}
              color="black"
              leftSection={<IconDownload size={14} />}
              onClick={() => setDownloadModalOpen(true)}
            >
              Download
            </Button>
            <Button
              radius={8}
              size="sm"
              variant="outline"
              bg={"white"}
              fw={500}
              color="black"
              onClick={goBack}
            >
              Back
            </Button>
          </Group>
        </Group>
        <Divider mt={"md"} size={2} />

        <Tabs.Panel value="daily">
          <DailyCutoffsTable
            {...data.dailyCutoffs}
            onLimitSelect={handleDailyCutoffsPageLimitSelect}
            onPageChange={handleDailyCutoffsPageChange}
            onDailyCutoffRowClick={onDailyCutoffRowClick}
            dailyCutoffRecordId={dailyCutoffRecordId}
            showLiters={showLiters}
          />
        </Tabs.Panel>

        <Tabs.Panel value="monthly">
          <MonthlyCutoffsTable
            {...data.monthlyCutoffs}
            onLimitSelect={handleMonthlyCutoffsPageLimitSelect}
            onPageChange={handleMonthlyCutoffsPageChange}
            showLiters={showLiters}
          />
        </Tabs.Panel>
      </Tabs>

      <DownloadFormatModal
        opened={downloadModalOpen}
        onClose={() => setDownloadModalOpen(false)}
        onDownload={handleDownloadReport}
      />
    </Box>
  );
}
